import { parseIsoAsWallClock } from '@/src/utils/dateUtil'
import { addDays, addMonths, endOfMonth, format, isSameDay, isValid, parseISO, startOfMonth, subDays } from 'date-fns'

const DATE_ONLY_FORMAT = /^\d{4}-\d{2}-\d{2}$/
const MIDNIGHT_UTC_TIME = 'T00:00:00.000Z'
const ISO_DATE_TIME_PARTS = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/
const ISO_TIMEZONE_SUFFIX = /(?:Z|[+-]\d{2}:?\d{2})$/
const GAP_CHECK_HOUR = 4

export type DataCompletenessDateInput = string | Date | null | undefined
export type DataCompletenessGranularity = string | null | undefined

export const isDataCompletenessDateOnlyString = (value?: string): value is string => !!value && DATE_ONLY_FORMAT.test(value)

export const serializeDataCompletenessDateInput = (value: DataCompletenessDateInput): string => {
  if (!value) return ''
  if (value instanceof Date) return isValid(value) ? value.toISOString() : ''
  return value
}

const buildCanonicalDateRange = (startDate: string, endDate: string) => {
  const parsedEndDate = parseISO(endDate)

  if (!isValid(parsedEndDate)) {
    return { startDate, endDate }
  }

  return {
    startDate: `${startDate}${MIDNIGHT_UTC_TIME}`,
    endDate: `${format(addDays(parsedEndDate, 1), 'yyyy-MM-dd')}${MIDNIGHT_UTC_TIME}`,
  }
}

const isMonthlyGranularity = (granularity?: DataCompletenessGranularity): boolean => granularity === 'month' || granularity === 'monthly'

const isStartOfLocalDay = (date: Date) =>
  date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0

const isEndOfLocalDay = (date: Date) =>
  date.getHours() === 23 && date.getMinutes() === 59 && date.getSeconds() === 59 && date.getMilliseconds() === 999

const parseSerializedLocalDayBoundary = (value: string) => {
  if (!ISO_TIMEZONE_SUFFIX.test(value)) return null

  const parsedDate = parseISO(value)
  if (!isValid(parsedDate)) return null

  return isStartOfLocalDay(parsedDate) || isEndOfLocalDay(parsedDate) ? parsedDate : null
}

const parseDataCompletenessDateInput = (value: DataCompletenessDateInput) => {
  if (!value) return null
  if (value instanceof Date) return isValid(value) ? value : null

  const parsedDate = parseSerializedLocalDayBoundary(value) ?? parseIsoDateTimeAsWallClock(value) ?? parseISO(value)
  return isValid(parsedDate) ? parsedDate : null
}

const formatDateTimeForRequest = (date: Date) => `${format(date, 'yyyy-MM-dd')}${MIDNIGHT_UTC_TIME}`

const hasTimezoneShiftedTime = (value: string) => {
  const dateTimeParts = value.match(ISO_DATE_TIME_PARTS)
  if (!dateTimeParts || !ISO_TIMEZONE_SUFFIX.test(value)) return false

  const [, , , , hours, minutes] = dateTimeParts
  return hours !== '00' || minutes !== '00'
}

const parseMonthlyStartDateInput = (value: DataCompletenessDateInput) => {
  const parsedDate = parseDataCompletenessDateInput(value)
  if (!parsedDate || typeof value !== 'string' || !hasTimezoneShiftedTime(value)) return parsedDate

  if (isSameDay(parsedDate, endOfMonth(parsedDate))) return addDays(parsedDate, 1)

  return parsedDate
}

const parseMonthlyEndDateInput = (value: DataCompletenessDateInput) => {
  const parsedDate = parseDataCompletenessDateInput(value)
  if (!parsedDate || typeof value !== 'string' || !hasTimezoneShiftedTime(value)) return parsedDate

  if (isSameDay(parsedDate, startOfMonth(parsedDate))) return subDays(parsedDate, 1)

  return parsedDate
}

const buildMonthlyDateRange = (startDate: string, endDate: string) => {
  const parsedStartDate = parseMonthlyStartDateInput(startDate)
  const parsedEndDate = parseMonthlyEndDateInput(endDate)

  if (!parsedStartDate || !parsedEndDate) {
    return { startDate, endDate }
  }

  return {
    startDate: formatDateTimeForRequest(startOfMonth(parsedStartDate)),
    endDate: formatDateTimeForRequest(addMonths(startOfMonth(parsedEndDate), 1)),
  }
}

export const normalizeDataCompletenessDateRange = (
  startDate?: string,
  endDate?: string,
  granularity?: DataCompletenessGranularity
): { startDate: string; endDate: string } => {
  if (!startDate || !endDate) {
    return { startDate: startDate ?? '', endDate: endDate ?? '' }
  }

  if (isMonthlyGranularity(granularity)) {
    return buildMonthlyDateRange(startDate, endDate)
  }

  const parsedStartDate = parseISO(startDate)
  const parsedEndDate = parseISO(endDate)

  if (!isValid(parsedStartDate) || !isValid(parsedEndDate)) {
    return { startDate, endDate }
  }

  if (isDataCompletenessDateOnlyString(startDate) && isDataCompletenessDateOnlyString(endDate)) {
    return buildCanonicalDateRange(startDate, endDate)
  }

  if (isStartOfLocalDay(parsedStartDate) && isEndOfLocalDay(parsedEndDate)) {
    return buildCanonicalDateRange(format(parsedStartDate, 'yyyy-MM-dd'), format(parsedEndDate, 'yyyy-MM-dd'))
  }

  return { startDate, endDate }
}

const formatDateOnlyWithTime = (date: string, time: string) => {
  const [year, month, day] = date.split('-')

  if (!year || !month || !day) return '-'

  return `${day}.${month}.${year} ${time}`
}

const parseIsoDateTimeAsWallClock = (value: string) => {
  if (!ISO_DATE_TIME_PARTS.test(value)) return null

  const parsedDate = parseIsoAsWallClock(value)
  if (!isValid(parsedDate)) return null

  return parsedDate
}

const formatIsoDateTimeWithoutTimezoneShift = (value: string) => {
  const parsedDate = parseIsoDateTimeAsWallClock(value)

  if (!parsedDate) return '-'

  return format(parsedDate, 'dd.MM.yyyy HH:mm')
}

const isMidnightToNextMidnightRange = (startDate: string, endDate: string) => {
  const startDateTimeParts = startDate.match(ISO_DATE_TIME_PARTS)
  const endDateTimeParts = endDate.match(ISO_DATE_TIME_PARTS)

  if (!startDateTimeParts || !endDateTimeParts) return false

  const [, startYear, startMonth, startDay, startHours, startMinutes] = startDateTimeParts
  const [, endYear, endMonth, endDay, endHours, endMinutes] = endDateTimeParts

  if (startHours !== '00' || startMinutes !== '00' || endHours !== '00' || endMinutes !== '00') return false

  const startDateOnly = `${startYear}-${startMonth}-${startDay}`
  const endDateOnly = `${endYear}-${endMonth}-${endDay}`
  const expectedEndDateOnly = format(addDays(parseISO(startDateOnly), 1), 'yyyy-MM-dd')

  return endDateOnly === expectedEndDateOnly
}

export const formatDataCompletenessTimeframe = (
  startDate?: DataCompletenessDateInput,
  endDate?: DataCompletenessDateInput,
  granularity?: DataCompletenessGranularity
) => {
  if (!startDate || !endDate) return '-'

  const serializedStartDate = serializeDataCompletenessDateInput(startDate)
  const serializedEndDate = serializeDataCompletenessDateInput(endDate)
  const parsedStartDate = parseDataCompletenessDateInput(startDate)
  const parsedEndDate = parseDataCompletenessDateInput(endDate)

  if (!parsedStartDate || !parsedEndDate) return '-'

  if (isMonthlyGranularity(granularity)) {
    const monthlyStartDate = parseMonthlyStartDateInput(startDate)
    const monthlyEndDate = parseMonthlyEndDateInput(endDate)
    if (!monthlyStartDate || !monthlyEndDate) return '-'

    return `${format(startOfMonth(monthlyStartDate), 'dd.MM.yyyy')} - ${format(
      subDays(addMonths(startOfMonth(monthlyEndDate), 1), 1),
      'dd.MM.yyyy'
    )}`
  }

  if (
    isDataCompletenessDateOnlyString(serializedStartDate) &&
    isDataCompletenessDateOnlyString(serializedEndDate) &&
    isSameDay(parsedStartDate, parsedEndDate)
  ) {
    return `${formatDateOnlyWithTime(serializedStartDate, '00:00')} - ${formatDateOnlyWithTime(serializedStartDate, '23:59')}`
  }

  if (isMidnightToNextMidnightRange(serializedStartDate, serializedEndDate)) {
    return `${formatIsoDateTimeWithoutTimezoneShift(serializedStartDate)} - ${formatDateOnlyWithTime(serializedStartDate.slice(0, 10), '23:59')}`
  }

  if (isSameDay(parsedStartDate, parsedEndDate)) {
    return `${format(parsedStartDate, 'dd.MM.yyyy HH:mm')} - ${format(parsedEndDate, 'dd.MM.yyyy HH:mm')}`
  }

  return `${format(parsedStartDate, 'dd.MM.yyyy')} - ${format(parsedEndDate, 'dd.MM.yyyy')}`
}

export const formatDataCompletenessGapCheckDate = (now = new Date()) => {
  const todayGapCheck = new Date(now)
  todayGapCheck.setHours(GAP_CHECK_HOUR, 0, 0, 0)
  const latestGapCheck = now < todayGapCheck ? subDays(todayGapCheck, 1) : todayGapCheck

  return format(latestGapCheck, 'dd.MM.yyyy')
}
